import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  ShieldCheckIcon, 
  CpuChipIcon, 
  ScaleIcon, 
  CommandLineIcon,
  CodeBracketSquareIcon,
  FingerPrintIcon,
  MagnifyingGlassIcon,
  MapIcon,
  RocketLaunchIcon
} from '@heroicons/react/24/outline';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import SchemaMarkup from '../components/SchemaMarkup';

export default function About() {
  const { t, i18n } = useTranslation();
  const langPrefix = i18n.language === 'et' ? '/et' : '/en';

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const valuesIcons = [ShieldCheckIcon, CpuChipIcon, ScaleIcon];
  const valuesList = [
    { title: t('about_page.values.v1_title'), desc: t('about_page.values.v1_desc') },
    { title: t('about_page.values.v2_title'), desc: t('about_page.values.v2_desc') },
    { title: t('about_page.values.v3_title'), desc: t('about_page.values.v3_desc') }
  ];

  // Process Steps - Updated Icons for a "Constructive" flow
  const processSteps = [
    { id: '01', title: t('about_page.process.steps.0.title'), desc: t('about_page.process.steps.0.desc') },
    { id: '02', title: t('about_page.process.steps.1.title'), desc: t('about_page.process.steps.1.desc') },
    { id: '03', title: t('about_page.process.steps.2.title'), desc: t('about_page.process.steps.2.desc') },
    { id: '04', title: t('about_page.process.steps.3.title'), desc: t('about_page.process.steps.3.desc') }
  ];

  const techStackObj = t('about_page.tech_stack', { returnObjects: true });
  const techList = techStackObj && typeof techStackObj === 'object' ? Object.values(techStackObj) : [];

  return (
    <div className="bg-slate-50 min-h-screen pb-20">
      <SEO title={t('about_page.title')} description={t('about_page.meta_desc')} />
      <SchemaMarkup type="about-page" data={{ title: t('about_page.title'), description: t('about_page.meta_desc'), url: `${langPrefix}/about` }} />

      <PageHeader title={t('about_page.title')} subtitle={t('about_page.subtitle')} maxWidth="max-w-6xl" />

      <div className="container mx-auto px-4 py-12 md:py-20 max-w-6xl">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          
          {/* LEFT: Philosophy (Positive Tone) */}
          <div className="lg:col-span-7 bg-white rounded-3xl p-8 md:p-12 shadow-xl shadow-slate-200/50 border border-slate-100">
            
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-8 flex items-center gap-2">
               <span className="w-8 h-[1px] bg-slate-300"></span> {t('about_page.story_section_title')}
            </h2>
            
            <div className="prose prose-lg prose-slate text-slate-600 leading-relaxed mb-12">
               <h3 className="text-xl font-bold text-slate-900 mb-4">{t('about_page.story.title')}</h3>
               <p className="mb-6">{t('about_page.story.p1')}</p>
               <p className="mb-6">{t('about_page.story.p2')}</p>
               <p>{t('about_page.story.p3')}</p>
            </div>

            <div className="mt-12 pt-12 border-t border-slate-100">
                <h3 className="text-xs font-bold text-slate-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                    <FingerPrintIcon className="w-4 h-4 text-cyan-600" /> {t('about_page.protocols_title')}
                </h3>
                <div className="grid gap-4">
                {valuesList.map((item, index) => {
                    const Icon = valuesIcons[index] || ShieldCheckIcon;
                    return (
                    <div key={index} className="group flex items-start gap-4 p-5 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-white hover:border-cyan-200 hover:shadow-lg transition-all duration-300">
                        <div className="w-10 h-10 rounded-lg bg-white border border-slate-200 flex items-center justify-center shadow-sm group-hover:border-cyan-400 group-hover:text-cyan-500 text-slate-400 transition-colors flex-shrink-0">
                          <Icon className="w-5 h-5" />
                        </div>
                        <div>
                        <h4 className="font-bold text-slate-900 text-sm uppercase tracking-wide mb-1 group-hover:text-cyan-700 transition-colors">{item.title}</h4>
                        <p className="text-sm text-slate-500 leading-relaxed">{item.desc}</p>
                        </div>
                    </div>
                    );
                })}
                </div>
            </div>
          </div>

          {/* RIGHT: Tech Stack */}
          <div className="lg:col-span-5 sticky top-8">
            <div className="bg-slate-900 rounded-3xl p-8 md:p-10 text-white shadow-2xl border border-slate-800 relative overflow-hidden">
               <div className="absolute top-0 right-0 p-4 opacity-5 pointer-events-none">
                  <CommandLineIcon className="w-40 h-40" />
               </div>
               <h3 className="text-xl font-bold mb-8 flex items-center gap-3 relative z-10">
                 <CodeBracketSquareIcon className="w-6 h-6 text-cyan-400" />
                 {t('about_page.tech_title')}
               </h3>
               <div className="flex flex-wrap gap-2 mb-10 relative z-10">
                 {techList.map((tech, i) => (
                   <span key={i} className="px-3 py-1.5 bg-slate-800 border border-slate-700 rounded-md text-xs font-mono font-bold text-cyan-300 hover:border-cyan-500 hover:text-white hover:bg-slate-700 transition-all cursor-default">
                     {tech}
                   </span>
                 ))}
               </div>
               <div className="relative z-10 pt-8 border-t border-slate-800">
                 <p className="text-sm text-slate-400 italic leading-relaxed font-light">"{t('about_page.tech_summary')}"</p>
               </div>
               <div className="absolute bottom-6 right-6 flex items-center gap-2 text-xs font-mono text-emerald-500 uppercase tracking-widest">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 motion-safe:animate-pulse"></span> {t('about_page.systems_status')}
               </div>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-4">
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-center">
                    <div className="text-3xl font-black text-slate-900">2021</div>
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-1">{t('about_page.stats.established')}</div>
                </div>
                <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm text-center">
                    <div className="text-3xl font-black text-slate-900">12+</div>
                    <div className="text-xs font-bold text-slate-400 uppercase tracking-wider mt-1">{t('about_page.stats.experience')}</div>
                </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- FOUNDER SECTION --- */}
      <div className="container mx-auto px-4 py-16 max-w-6xl">
        <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/50 p-8 md:p-12 flex flex-col md:flex-row items-center gap-10 md:gap-16">
          <div className="flex-shrink-0 flex flex-col items-center text-center md:text-left md:items-start">
            <div className="w-28 h-28 rounded-2xl bg-slate-900 flex items-center justify-center text-cyan-400 text-4xl font-black font-mono mb-4 border border-slate-800 shadow-lg">
              JL
            </div>
            <div className="font-bold text-slate-900 text-lg">{t('about_page.founder.name')}</div>
            <div className="text-slate-500 text-sm">{t('about_page.founder.role')}</div>
            <a href="https://www.linkedin.com/company/mupax-oy" target="_blank" rel="noopener noreferrer" className="mt-4 inline-flex items-center gap-2 text-xs font-bold text-blue-600 hover:text-blue-500 transition-colors">
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/></svg>
              LinkedIn
            </a>
          </div>
          <div className="flex-1">
            <h2 className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-4 flex items-center gap-2">
              <span className="w-8 h-[1px] bg-slate-300"></span> {t('about_page.founder.section_label')}
            </h2>
            <p className="text-slate-700 leading-relaxed text-lg mb-4">{t('about_page.founder.bio')}</p>
            <p className="text-slate-500 leading-relaxed">{t('about_page.founder.bio2')}</p>
          </div>
        </div>
      </div>

      {/* --- SECTION 2: THE PROCESS --- */}
      <div className="bg-slate-900 py-24 relative overflow-hidden">
         <div className="absolute inset-0 opacity-10" style={{ backgroundImage: 'linear-gradient(#22d3ee 1px, transparent 1px), linear-gradient(90deg, #22d3ee 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>
         
         <div className="container mx-auto px-4 max-w-6xl relative z-10">
            <div className="text-center mb-16">
                <h2 className="text-sm font-bold text-cyan-400 uppercase tracking-[0.2em] mb-4">{t('about_page.process.label')}</h2>
                <h3 className="text-3xl md:text-4xl font-extrabold text-white">{t('about_page.process.title')}</h3>
                <p className="text-slate-400 mt-4 max-w-2xl mx-auto">{t('about_page.process.subtitle')}</p>
            </div>

            <div className="grid md:grid-cols-4 gap-8">
                {processSteps.map((step, i) => {
                    // Re-mapping icons inside mapping logic for safety
                    const icons = [MagnifyingGlassIcon, MapIcon, CommandLineIcon, RocketLaunchIcon];
                    const Icon = icons[i] || MagnifyingGlassIcon;
                    return (
                        <div key={i} className="relative group">
                            {i !== processSteps.length - 1 && (
                                <div className="hidden md:block absolute top-8 left-1/2 w-full h-[2px] bg-slate-800 z-0"></div>
                            )}
                            <div className="relative z-10 flex flex-col items-center text-center">
                                <div className="w-16 h-16 rounded-2xl bg-slate-800 border border-slate-700 flex items-center justify-center text-white mb-6 group-hover:border-cyan-500 group-hover:shadow-[0_0_20px_rgba(6,182,212,0.3)] transition-all duration-300">
                                    <Icon className="w-8 h-8 text-cyan-400" />
                                </div>
                                <div className="text-xs font-mono font-bold text-slate-500 mb-2">STEP 0{i + 1}</div>
                                <h4 className="text-lg font-bold text-white mb-3">{step.title}</h4>
                                <p className="text-sm text-slate-400 leading-relaxed">{step.desc}</p>
                            </div>
                        </div>
                    );
                })}
            </div>
         </div>
      </div>
    </div>
  );
}
